import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { all_routes } from "../../../router/all_routes";
import {
  gender,
} from "../../../../core/common/selectoption/selectoption";
import CommonSelect from "../../../../core/common/commonSelect";
import { useStudent } from "../hooks/useStudent";
import { StudentPayloadType } from "../types/studentRequest";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { showToast } from "../../../../core/utils/alert";
import { useActiveSession } from "../../../../utils/getActiveSession";
import { useBatchTiming } from "../../../configuration/batch-timing/hooks/useBatchTiming";
import { useCenter } from "../../centers/hooks/useCenter";
import StudentModals from "../studentModals";
import { useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectUserRole,
} from "../../../../core/data/slice/authSlice";

/**
 * AddStudent Component
 * Comprehensive form for adding/editing student information in the school management system.
 * Handles student personal details, family info, academic records, and medical history.
 */
const AddStudent = () => {
  // Get user role and info from Redux store
  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = userRole === "superadmin";

  const activeSession = useActiveSession();
  const sessionId = activeSession?.id || 0;
  const sessionName = activeSession?.name;
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    updateStudent,
    useGetStudentById,
    isUpdating,
    addError,
    updateError,
    isAdding,
  } = useStudent();
  const studentData = useGetStudentById(id || "");
  const { getCourseState } = useCourse({ published: true });
  const { courses } = getCourseState();

  const courseOptions = courses.map((course) => ({
    value: course.id.toString(),
    label: course.name,
    id: course.id,
  }));
  // Add batch timings fetch
  const { batchTimings, isLoading: isBatchTimingsLoading } = useBatchTiming();

  // Convert batch timings to select options with days included
  const batchTimingOptions = batchTimings.map((batch) => ({
    id: batch.id,
    value: batch.id.toString(),
    label: `${batch.start_time} - ${batch.end_time} [${batch.days?.join(
      ", "
    )}]`,
    description: batch.days?.join(", "),
  }));

  // Always call the hook, but skip the API call if not a superadmin
  const { getCenterState } = useCenter({
    skip: userRole !== "superadmin",
  });
  const { centers = [] } = isSuperAdmin ? getCenterState() : { centers: [] };

  // Convert centers to select options (only used by superadmin)
  const centerOptions = isSuperAdmin
    ? centers.map((center) => ({
      value: center.id.toString(),
      label: center.name,
    }))
    : [];

  /** State Management */
  // Controls if form is in edit mode
  const [isEdit, setIsEdit] = useState<boolean>(false);
  // Default date for date picker fields
  const [defaultDate, setDefaultDate] = useState<dayjs.Dayjs | null>(null);
  // Tracks Aadhar verification status
  const [isAadharVerified, setIsAadharVerified] = useState<boolean>(false);
  // Controls visibility of OTP input field
  const [showOtpField, setShowOtpField] = useState<boolean>(false);

  // State for form fields
  const [formData, setFormData] = useState<StudentPayloadType>({
    first_name: "",
    last_name: "",
    profile: {
      gender: "",
      dob: "",
      fathers_name: "",
      fathers_occupation: "",
      email: "",
      phone: undefined,
      emergency_contact: undefined,
      education: "test",
      institution_name: "",
      class: "",
      aadhar_number: undefined,
    },
    address: {
      street: "",
      landmark: "",
      city: "",
      pincode: undefined,
      district: "",
      state: "",
      country: "",
    },
    status: "active",
    center_id: currentUser?.center_id ?? 0,
    course_id: 0,
    stage_id: 0,
    session_id: sessionId,
    batch_id: null,
  });

  // Add filtered batch timings state
  const [filteredBatchTimings, setFilteredBatchTimings] = useState<
    typeof batchTimings
  >([]);

  // Add validation states for number fields
  const [formErrors, setFormErrors] = useState({
    phone: "",
    emergency_contact: "",
    pincode: "",
    aadhar_number: "",
    dob: "",
  });

  // Add state for DatePicker focused status
  const [isDatePickerFocused, setIsDatePickerFocused] = useState(false);

  // States for geographic data
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);
  const [states, setStates] = useState<{ value: string; label: string }[]>([]);
  const [cities, setCities] = useState<{ value: string; label: string }[]>([]);

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch("https://countriesnow.space/api/v0.1/countries/iso");
        const result = await response.json();
        if (!result.error) {
          const countryOptions = result.data.map((c: any) => ({
            value: c.name,
            label: c.name,
            iso2: c.Iso2
          }));
          setCountries(countryOptions);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (!formData.address.country) {
        setStates([]);
        return;
      }
      try {
        const response = await fetch("https://countriesnow.space/api/v0.1/countries/states", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ country: formData.address.country }),
        });
        const result = await response.json();
        if (!result.error) {
          const stateOptions = result.data.states.map((s: any) => ({
            value: s.name,
            label: s.name,
          }));
          setStates(stateOptions);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    fetchStates();
  }, [formData.address.country]);

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!formData.address.state || !formData.address.country) {
        setCities([]);
        return;
      }
      try {
        const response = await fetch("https://countriesnow.space/api/v0.1/countries/state/cities", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            country: formData.address.country,
            state: formData.address.state,
          }),
        });
        const result = await response.json();
        if (!result.error) {
          const cityOptions = result.data.map((c: any) => ({
            value: c,
            label: c,
          }));
          setCities(cityOptions);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, [formData.address.state, formData.address.country]);

  // Add this validation function
  const validateAge = (date: string) => {
    if (!date) return true;

    const birthDate = dayjs(date);
    const today = dayjs();

    // Calculate exact dates for 5 and 20 years ago
    const minimumDate = today.subtract(20, "year").startOf("day");
    const maximumDate = today.subtract(5, "year").startOf("day");

    // Check if birthdate is outside the valid range
    const isTooOld = birthDate.isBefore(minimumDate);
    const isTooYoung = birthDate.isAfter(maximumDate);

    let errorMessage = "";
    if (isTooYoung) {
      errorMessage = `Student must not be born after ${maximumDate.format(
        "DD-MM-YYYY"
      )} (min age: 5 years)`;
    } else if (isTooOld) {
      errorMessage = `Student must not be born before ${minimumDate.format(
        "DD-MM-YYYY"
      )} (max age: 20 years)`;
    }

    // Update the form errors state
    setFormErrors((prev) => ({
      ...prev,
      dob: errorMessage,
    }));

    return errorMessage === "";
  };

  // Add function to validate number fields
  const validateNumberField = (value: string, field: string): boolean => {
    // Check if value is a valid number
    if (value === "") return true; // Allow empty values

    const numberRegex = /^[0-9]+$/;
    const isValid = numberRegex.test(value);

    // Additional specific validations
    let errorMessage = "";

    if (!isValid) {
      // Use field-specific error messages
      switch (field) {
        case "phone":
          errorMessage = "Please enter a valid phone number.";
          break;
        case "emergency_contact":
          errorMessage = "Please enter a valid contact number.";
          break;
        case "aadhar_number":
          errorMessage = "Please enter a valid Aadhar number.";
          break;
        case "pincode":
          errorMessage = "Please enter a valid pincode.";
          break;
        default:
          errorMessage = "Please enter numbers only";
      }
    } else {
      // Field-specific validations
      switch (field) {
        case "phone":
        case "emergency_contact":
          if (value.length !== 10) {
            errorMessage = "Phone number must be exactly 10 digits";
          }
          break;
        case "aadhar_number":
          if (value.length !== 12) {
            errorMessage = "Aadhar number must be exactly 12 digits";
          }
          break;
        case "pincode":
          if (value.length !== 6) {
            errorMessage = "Pincode must be exactly 6 digits";
          }
          break;
      }
    }

    // Update error state
    setFormErrors((prev) => ({
      ...prev,
      [field]: errorMessage,
    }));

    return errorMessage === "";
  };

  /**
   * Initializes form based on route
   * - Sets default values when in edit mode
   * - Resets form when in add mode
   */
  useEffect(() => {
    if (studentData && id) {
      setIsEdit(true);
      setFormData((prev) => ({
        ...prev,
        session_id: sessionId,
        first_name: studentData.first_name,
        last_name: studentData.last_name,
        profile: {
          gender: studentData.profile.gender,
          dob: studentData.profile.dob,
          fathers_name: studentData.profile.fathers_name,
          fathers_occupation: studentData.profile.fathers_occupation,
          email: studentData.profile.email,
          phone: studentData.profile.phone || undefined,
          emergency_contact: studentData.profile.emergency_contact || undefined,
          education: studentData.profile.education,
          institution_name: studentData.profile.institution_name,
          class: studentData.profile.class,
          aadhar_number: studentData.profile.aadhar_number || undefined,
        },
        address: {
          street: studentData.address.street,
          landmark: studentData.address.landmark,
          city: studentData.address.city,
          pincode: studentData.address.pincode || undefined,
          district: studentData.address.district,
          state: studentData.address.state,
          country: studentData.address.country,
        },
        status: studentData.status,
        center_id: isSuperAdmin
          ? studentData.center.id
          : currentUser?.center_id ?? 0, // Use nullish coalescing
        course_id: studentData.course.id,
        stage_id: studentData.course.stage.id,
        batch_id: studentData.batch?.id || null,
      }));
    } else {
      setIsEdit(false);
      setDefaultDate(null);
      setFormData((prev) => ({
        ...prev,
        session_id: sessionId,
        center_id: isSuperAdmin ? 0 : currentUser?.center_id ?? 0, // Use nullish coalescing
      }));
    }
  }, [studentData, id, sessionId, isSuperAdmin, currentUser?.center_id]);

  // Add this useEffect to filter batch timings when in edit mode
  useEffect(() => {
    if (studentData && id && formData.course_id) {
      // Filter batch timings for the selected course and center (for super admin)
      const filteredTimings = batchTimings.filter((batch) => {
        const courseMatch = batch.course_id === formData.course_id;
        const centerMatch = isSuperAdmin
          ? batch.center_id === formData.center_id
          : true;
        return courseMatch && centerMatch;
      });
      setFilteredBatchTimings(filteredTimings);
    }
  }, [studentData, id, formData.course_id, formData.center_id, batchTimings, isSuperAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const response = await updateStudent(id!, formData);
    // console.log(response, "response");
    if (response.data) {
      showToast({
        title: "Success",
        text: "Student updated successfully",
        icon: "success",
      });
      navigate(routes.studentList);
    } else {
      showToast({
        title: "Error",
        text: response.error || "Student update failed",
        icon: "error",
      });
    }
  };

  // Update form data handler
  const handleInputChange = (path: string, value: string | number | undefined | null) => {
    if (path === "profile.dob") {
      validateAge(value as string);
    }

    // Handle number fields validation
    if (
      [
        "profile.phone",
        "profile.emergency_contact",
        "profile.aadhar_number",
        "address.pincode",
      ].includes(path)
    ) {
      const fieldName = path.split(".")[1];
      const isValid = validateNumberField(value?.toString() || "", fieldName);

      // If valid, convert to number or undefined if empty
      if (isValid) {
        value = (value === "" || value === undefined || value === null) ? undefined : Number(value);
      }
    }

    setFormData((prev) => {
      const keys = path.split(".");
      const newData = { ...prev };
      let current: any = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const handleCenterChange = (
    value: { value: string; label: string } | null
  ) => {
    const centerId = Number(value?.value) || 0;
    const centerName = value?.label || "";

    handleInputChange("center_id", centerId);
    handleInputChange("center_name", centerName);

    // Filter batch timings when center changes (for super admin)
    if (formData.course_id) {
      const filteredTimings = batchTimings.filter((batch) => {
        const courseMatch = batch.course_id === formData.course_id;
        const centerMatch = batch.center_id === centerId;
        return courseMatch && centerMatch;
      });
      setFilteredBatchTimings(filteredTimings);

      // Reset batch_id if current batch is not in filtered list
      if (formData.batch_id && !filteredTimings.find(b => b.id === formData.batch_id)) {
        handleInputChange("batch_id", null);
      }
    }
  };

  // Update handleBatchTimingChange to use the formatted label
  const handleBatchTimingChange = (
    value: { value: string; label: string } | null
  ) => {
    const batchId = Number(value?.value) || null;
    const batchName = value?.label || "";

    // Update both batch_timing (with name) and batch_ids
    handleInputChange("profile.batch_timing", batchName);
    handleInputChange("batch_id", batchId);
  };

  // Update handleCourseChange to not set batch_timing
  const handleCourseChange = (value: { value: string } | null) => {
    const courseId = Number(value?.value) || 0;
    handleInputChange("course_id", courseId);

    // Find selected course and set first stage automatically
    const selectedCourse = courses.find((course) => course.id === courseId);
    if (selectedCourse && selectedCourse.courseStages?.length > 0) {
      // Set the first stage ID automatically
      handleInputChange("stage_id", selectedCourse.courseStages[0].id);

      // Filter batch timings for selected course and center (for super admin)
      const filteredTimings = batchTimings.filter((batch) => {
        const courseMatch = batch.course_id === courseId;
        const centerMatch = isSuperAdmin
          ? batch.center_id === formData.center_id
          : true;
        return courseMatch && centerMatch;
      });
      setFilteredBatchTimings(filteredTimings);

      // Reset only batch_id
      handleInputChange("batch_id", null);
    } else {
      handleInputChange("stage_id", 0);
      setFilteredBatchTimings([]);
      handleInputChange("batch_id", null);
    }
  };

  // Function to check if all required fields are filled
  const isFormValid = () => {
    const { first_name, profile, address, center_id, course_id, batch_id } =
      formData;

    // Check if there are any validation errors
    const hasValidationErrors = Object.values(formErrors).some(
      (error) => error !== ""
    );

    return (
      first_name?.trim() !== "" &&
      profile.gender !== "" &&
      profile.email?.trim() !== "" &&
      profile.phone !== undefined &&
      profile.phone !== 0 &&
      profile.class?.trim() !== "" &&
      address.country !== "" &&
      address.state !== "" &&
      address.city !== "" &&
      address.district !== "" &&
      center_id !== 0 &&
      course_id !== 0 &&
      !hasValidationErrors
    );
  };

  // console.log(formData, "--formData");
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">{isEdit ? "Edit" : "Add"} Student</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.studentList}>Students</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Add"} Student
                  </li>
                </ol>
              </nav>
              {/* Display Active Session Name */}
              {sessionName && (
                <div className="alert alert-info mt-2 p-3 rounded-3 shadow-sm border-0">
                  <strong className="text-dark">Session:</strong>{" "}
                  <span className="text-primary fw-bold">{sessionName}</span>
                </div>
              )}
            </div>
          </div>
          {/* /Page Header */}
          <div className="row">
            <div className="col-md-12">
              <form>
                {/* Aadhar Authentication Section 
                    Handles verification of student's Aadhar number through OTP */}
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-id fs-16" />
                      </span>
                      <h4 className="text-dark">Aadhar Authentication</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Aadhar Number
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.aadhar_number ? "is-invalid" : ""
                              }`}
                            placeholder="Enter 12-digit Aadhar number"
                            maxLength={12}
                            value={
                              formData.profile.aadhar_number === undefined || formData.profile.aadhar_number === 0
                                ? ""
                                : formData.profile.aadhar_number
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "profile.aadhar_number",
                                e.target.value
                              )
                            }
                          />
                          {formErrors.aadhar_number && (
                            <div className="invalid-feedback">
                              {formErrors.aadhar_number}
                            </div>
                          )}
                        </div>
                      </div>
                      {!isEdit && (
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">&nbsp;</label>
                            <div>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => setShowOtpField(true)}
                              >
                                Verify Aadhar
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {showOtpField && (
                        <>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Enter OTP</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter 6-digit OTP"
                                maxLength={6}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">&nbsp;</label>
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={() => setIsAadharVerified(true)}
                                >
                                  Verify OTP
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* Personal Information Section
                    Essential student details including:
                    - Student photo upload (4MB max, JPG/PNG/SVG)
                    - Full name
                    - Father's name
                    - Gender
                    - Date of birth */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-info-square-rounded fs-16" />
                      </span>
                      <h4 className="text-dark">Personal Information</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    {/* Photo Upload Section */}

                    {/* Personal Details Form Fields */}
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            First Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter first name"
                            value={formData.first_name}
                            onChange={(e) =>
                              handleInputChange("first_name", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Last Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter last name"
                            value={formData.last_name}
                            onChange={(e) =>
                              handleInputChange("last_name", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Father's Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Father's name"
                            value={formData.profile.fathers_name}
                            onChange={(e) =>
                              handleInputChange(
                                "profile.fathers_name",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Father's Occupation
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Father's Occupation"
                            value={formData.profile.fathers_occupation}
                            onChange={(e) =>
                              handleInputChange(
                                "profile.fathers_occupation",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Gender <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={gender}
                            value={
                              gender.find(
                                (option) =>
                                  option.value === formData.profile.gender
                              ) || undefined
                            }
                            onChange={(value) =>
                              handleInputChange(
                                "profile.gender",
                                value?.value || ""
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Date of Birth
                          </label>
                          <div className="position-relative">
                            <div className="input-icon position-relative">
                              <DatePicker
                                className={`form-control datetimepicker ${formErrors.dob ? "is-invalid" : ""
                                  }`}
                                style={{
                                  backgroundImage: "none",
                                  boxShadow: isDatePickerFocused
                                    ? formErrors.dob
                                      ? "0 0 0 0.25rem rgba(220, 53, 69, 0.25)"
                                      : "none"
                                    : "none",
                                }}
                                format="DD-MM-YYYY"
                                value={
                                  formData.profile.dob
                                    ? dayjs(formData.profile.dob)
                                    : undefined
                                }
                                onChange={(date) => {
                                  const formattedDate =
                                    date?.format("YYYY-MM-DD") || "";
                                  handleInputChange(
                                    "profile.dob",
                                    formattedDate
                                  );
                                }}
                                onFocus={() => setIsDatePickerFocused(true)}
                                onBlur={() => setIsDatePickerFocused(false)}
                                placeholder="Select Date"
                                popupStyle={{ zIndex: 1060 }}
                                disabledDate={(current) =>
                                  current && current > dayjs().endOf("day")
                                }
                              />
                              <span className="input-icon-addon">
                                <i
                                  className={`ti ti-calendar ${formErrors.dob ? "text-danger" : ""
                                    }`}
                                />
                              </span>
                            </div>
                            {formErrors.dob && (
                              <div className="invalid-feedback d-block">
                                {formErrors.dob}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Center & Course Details Section */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-book fs-16" />
                      </span>
                      <h4 className="text-dark">
                        {isSuperAdmin
                          ? "Center & Course Details"
                          : "Course Details"}
                      </h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      {isSuperAdmin && (
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Center <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              options={centerOptions.map((option) => ({
                                value: option.value.toString(),
                                label: option.label,
                              }))}
                              value={centerOptions.find(
                                (option) =>
                                  option.value === formData.center_id.toString()
                              )}
                              onChange={handleCenterChange}
                              placeholder="Select Center"
                            />
                          </div>
                        </div>
                      )}
                      <div className={`col-md-${isSuperAdmin ? "6" : "6"}`}>
                        <div className="mb-3">
                          <label className="form-label">
                            Select Course <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            placeholder="Select Course"
                            className="select"
                            options={courseOptions.map((option) => ({
                              value: option.value.toString(),
                              label: option.label,
                            }))}
                            value={courseOptions.find(
                              (option) =>
                                option.value === formData?.course_id?.toString()
                            )}
                            onChange={handleCourseChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Preferred Batch Timing
                          </label>
                          <CommonSelect
                            className="select"
                            options={filteredBatchTimings.map((batch) => ({
                              value: batch.id.toString(),
                              label: `${batch.start_time} - ${batch.end_time}`,
                              description: `[${batch.days?.join(", ")}]`,
                            }))}
                            value={
                              formData.batch_id
                                ? {
                                  value: formData.batch_id.toString(),
                                  label:
                                    filteredBatchTimings.find(
                                      (batch) =>
                                        batch.id === formData.batch_id
                                    )?.start_time +
                                    " - " +
                                    filteredBatchTimings.find(
                                      (batch) =>
                                        batch.id === formData.batch_id
                                    )?.end_time || "",
                                }
                                : undefined
                            }
                            onChange={handleBatchTimingChange}
                            isDisabled={!formData.course_id}
                            placeholder="Select Batch Timing"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Contact Details Section */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-phone fs-16" />
                      </span>
                      <h4 className="text-dark">Contact Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Email Address <span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Enter email address"
                            value={formData.profile.email}
                            onChange={(e) =>
                              handleInputChange("profile.email", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Mobile Number <span className="text-danger">*</span>
                          </label>
                          <input
                            type="tel"
                            className={`form-control ${formErrors.phone ? "is-invalid" : ""
                              }`}
                            placeholder="Enter mobile number"
                            maxLength={10}
                            value={
                              formData.profile.phone === undefined || formData.profile.phone === 0
                                ? ""
                                : formData.profile.phone
                            }
                            onChange={(e) =>
                              handleInputChange("profile.phone", e.target.value)
                            }
                          />
                          {formErrors.phone && (
                            <div className="invalid-feedback">
                              {formErrors.phone}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Emergency Contact Number
                          </label>
                          <input
                            type="tel"
                            className={`form-control ${formErrors.emergency_contact ? "is-invalid" : ""
                              }`}
                            placeholder="Enter emergency contact number"
                            maxLength={10}
                            value={
                              formData.profile.emergency_contact === undefined || formData.profile.emergency_contact === 0
                                ? ""
                                : formData.profile.emergency_contact
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "profile.emergency_contact",
                                e.target.value
                              )
                            }
                          />
                          {formErrors.emergency_contact && (
                            <div className="invalid-feedback">
                              {formErrors.emergency_contact}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Education Details Section */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-book fs-16" />
                      </span>
                      <h4 className="text-dark">Education Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            School/College Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter institution name"
                            value={formData.profile.institution_name}
                            onChange={(e) =>
                              handleInputChange(
                                "profile.institution_name",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Class <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter class"
                            value={formData.profile.class}
                            onChange={(e) =>
                              handleInputChange("profile.class", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-map fs-16" />
                      </span>
                      <h4 className="text-dark">Address</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Country <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={countries}
                            value={countries.find(
                              (option) =>
                                option.value === formData.address.country
                            )}
                            onChange={(value) => {
                              handleInputChange(
                                "address.country",
                                value?.value || ""
                              );
                              handleInputChange("address.state", "");
                              handleInputChange("address.city", "");
                            }}
                            placeholder="Select Country"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            State <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={states}
                            value={states.find(
                              (option) =>
                                option.value === formData.address.state
                            )}
                            onChange={(value) => {
                              handleInputChange(
                                "address.state",
                                value?.value || ""
                              );
                              handleInputChange("address.city", "");
                            }}
                            isDisabled={!formData.address.country}
                            placeholder="Select State"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            City <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={cities}
                            value={cities.find(
                              (option) => option.value === formData.address.city
                            )}
                            onChange={(value) =>
                              handleInputChange(
                                "address.city",
                                value?.value || ""
                              )
                            }
                            isDisabled={!formData.address.state}
                            placeholder="Select City"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            District <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter District"
                            value={formData.address.district}
                            onChange={(e) =>
                              handleInputChange("address.district", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Street Address
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter street address"
                            value={formData.address.street}
                            onChange={(e) =>
                              handleInputChange(
                                "address.street",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Landmark
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter landmark"
                            value={formData.address.landmark}
                            onChange={(e) =>
                              handleInputChange(
                                "address.landmark",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Postal Code
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.pincode ? "is-invalid" : ""
                              }`}
                            placeholder="Enter postal code"
                            maxLength={6}
                            value={
                              formData.address.pincode === undefined || formData.address.pincode === 0
                                ? ""
                                : formData.address.pincode
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "address.pincode",
                                e.target.value
                              )
                            }
                          />
                          {formErrors.pincode && (
                            <div className="invalid-feedback">
                              {formErrors.pincode}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Login Details Section */}
                {/* <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-map fs-16" />
                      </span>
                      <h4 className="text-dark">Login Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Username</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Username"
                            value={
                              formData.login_details.username || "username"
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "login_details.username",
                                e.target.value
                              )
                            }
                            disabled={isEdit}
                            // required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Password</label>
                          <input
                            type="password"
                            className="form-control"
                            placeholder="Enter Password"
                            value={formData.login_details.password}
                            onChange={(e) =>
                              handleInputChange(
                                "login_details.password",
                                e.target.value
                              )
                            }
                            disabled={isEdit}
                            // required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}

                {/* /Other Details */}
                <div className="text-end">
                  <Link to={routes.studentList} className="btn btn-light me-3">
                    Cancel
                  </Link>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isUpdating || !isFormValid()}
                    {...(!isEdit && {
                      "data-bs-toggle": "modal",
                      "data-bs-target": "#upload_modal",
                    })}
                    {...(isEdit && {
                      onClick: handleSubmit,
                    })}
                  >
                    {isAdding
                      ? "Saving..."
                      : isUpdating
                        ? "Updating..."
                        : isEdit
                          ? "Update Student"
                          : "Add Student"}
                  </button>
                  {addError && (
                    <div className="alert alert-danger mt-3">
                      Error: {String(addError)}
                    </div>
                  )}
                  {updateError && (
                    <div className="alert alert-danger mt-3">
                      Error: {String(updateError)}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <StudentModals studentData={formData} />
    </>
  );
};

export default AddStudent;
